import { isbot } from "isbot"
import { preferredLanguage } from "./accept-language"
import {
  MARKET_LANGUAGE_VARIANTS,
  composeMarketSlug,
  defaultLanguageFor,
  parseMarketSlug,
  type Language,
} from "./languages"
import { retiredMarketsReplacedBy } from "./retired-markets"

export interface LanguageRedirectInput {
  /** First path segment, e.g. "de" or "de-en". */
  marketSegment: string
  /** Raw Accept-Language header. */
  acceptLanguage: string | null
  /** Raw User-Agent header. */
  userAgent: string | null
  /** Parsed lang_pref cookie (market → language), or null. */
  langPref: Record<string, string> | null
  /** Parsed lang_seen cookie (market → true), or null. */
  langSeen: Record<string, boolean> | null
}

export interface RedirectDecision {
  /** Slug to route to, e.g. "de-en". Caller composes the full path. */
  targetSegment: string
  /** Base market to mark as seen, e.g. "de". */
  setSeenForMarket: string
}

/** Parse the lang_pref cookie. Returns null on absence or malformed input. */
export const parseLangCookie = (
  raw: string | null | undefined
): Record<string, string> | null => {
  if (!raw) return null
  try {
    const parsed: unknown = JSON.parse(raw)
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      Array.isArray(parsed)
    ) {
      return null
    }
    const out: Record<string, string> = {}
    for (const [k, v] of Object.entries(parsed)) {
      if (typeof v === "string") out[k] = v
    }
    return out
  } catch {
    return null
  }
}

/** Parse the lang_seen cookie. Returns null on absence or malformed input. */
export const parseSeenCookie = (
  raw: string | null | undefined
): Record<string, boolean> | null => {
  if (!raw) return null
  try {
    const parsed: unknown = JSON.parse(raw)
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      Array.isArray(parsed)
    ) {
      return null
    }
    const out: Record<string, boolean> = {}
    for (const [k, v] of Object.entries(parsed)) {
      if (v === true) out[k] = true
    }
    return out
  } catch {
    return null
  }
}

export const serializeLangPref = (map: Record<string, string>): string =>
  JSON.stringify(map)

export const serializeSeen = (map: Record<string, boolean>): string =>
  JSON.stringify(map)

export const resolveLanguageRedirect = (
  input: LanguageRedirectInput
): RedirectDecision | null => {
  // Gate 1: bots are never redirected (SEO/hreflang integrity).
  if (isbot(input.userAgent ?? "")) return null

  const { market, language } = parseMarketSlug(input.marketSegment)

  // Gate 2: loop guard — a segment already carrying a non-default language
  // is a resolved URL; leave it alone.
  if (language !== defaultLanguageFor(market)) return null

  // The languages this market can serve: its default first, then its variants.
  // Gates 3 and 5 both validate against this same set.
  const marketLanguages: Language[] = [
    defaultLanguageFor(market),
    ...MARKET_LANGUAGE_VARIANTS.filter((v) => v.market === market).map(
      (v) => v.language
    ),
  ]

  // Gate 3: explicit pick wins permanently — but only if it names a language
  // actually registered for this market. A tampered/legacy cookie like
  // lang_pref={"de":"fr"} must NOT compose a dead "de-fr" slug; ignore it and
  // fall through to the browser-sniff gates below.
  //
  // A pick made under a market that has since been RETIRED counts as this
  // market's pick (DEV-10186). `/de-en` shoppers hold `{"de":"en"}` and land on
  // the bare `/eu`, where "English" is the default and therefore invisible —
  // without inheriting it, the sniff below would read a German browser and move
  // them to `/eu-de`, undoing the English they explicitly asked for. The
  // `next.config.ts` redirect cannot write a cookie to say so, so the successor
  // market reads its predecessor's instead. Only a pick is inherited, never
  // "seen": a legacy visitor who never chose a language should still get the
  // guess, and for a German speaker that guess is right.
  const pick =
    input.langPref?.[market] ??
    retiredMarketsReplacedBy(market)
      .map((retired) => input.langPref?.[retired])
      .find(Boolean)
  if (pick && marketLanguages.includes(pick as Language)) {
    const target = composeMarketSlug(market, pick as Language)
    if (target === input.marketSegment) return null
    return { targetSegment: target, setSeenForMarket: market }
  }

  // Gate 4: first-visit only — do not auto-redirect a market seen before.
  if (input.langSeen?.[market]) return null

  // Gate 5: capability + preference.
  const preferred = preferredLanguage(input.acceptLanguage, marketLanguages)
  if (!preferred || preferred === defaultLanguageFor(market)) return null

  return {
    targetSegment: composeMarketSlug(market, preferred),
    setSeenForMarket: market,
  }
}
